'use client';
import React, { useState, useEffect } from 'react';
import { Sparkles, CheckCircle2, AlertTriangle, Info, Clock, ArrowRight, ChevronLeft, ChevronDown, ChevronUp } from 'lucide-react';
import type { WizardData } from './VisaWizard';

interface Props {
  data: WizardData;
  updateData: (d: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const requiredDocs = [
  { id: 'req-passport', label: 'Valid passport', detail: 'Minimum 6 months validity beyond your travel date. At least 2 blank pages required.', critical: true },
  { id: 'req-photo', label: 'Biometric passport photos (×2)', detail: '35mm × 45mm, white background, taken within 6 months. No glasses.', critical: true },
  { id: 'req-bank', label: 'Bank statement (last 3 months)', detail: 'Must show sufficient funds: minimum €3,000 for a 30-day stay. Stamped by your bank.', critical: true },
  { id: 'req-insurance', label: 'Travel insurance certificate', detail: 'Minimum €30,000 coverage, valid for all Schengen countries. Dates must match your travel dates.', critical: true },
  { id: 'req-itinerary', label: 'Confirmed flight itinerary', detail: 'Round-trip booking confirmation. Does not need to be a purchased ticket — a booking reservation is accepted.', critical: true },
  { id: 'req-hotel', label: 'Hotel / accommodation confirmation', detail: 'Booking confirmation for all nights of stay. Airbnb receipts are accepted.', critical: true },
  { id: 'req-cover', label: 'Cover letter (AI-generated)', detail: 'A formal letter addressing the embassy, explaining your purpose, itinerary, and ties to your home country.', critical: true, aiGenerated: true },
  { id: 'req-employment', label: 'Employment letter or proof of income', detail: 'Letter from employer on company letterhead confirming your position, salary, and approved leave.', critical: false },
];

const optionalDocs = [
  { id: 'opt-invitation', label: 'Invitation letter (if visiting friends/family)', detail: 'Letter from your host in Germany with their address and contact details.' },
  { id: 'opt-noc', label: 'No Objection Certificate (students)', detail: 'From your university or college confirming enrollment and approved leave.' },
  { id: 'opt-property', label: 'Property ownership documents', detail: 'Strengthens your ties to home country — mortgage deeds, land title, etc.' },
  { id: 'opt-prev-visa', label: 'Previous Schengen visa copies', detail: 'Copies of any previous Schengen visas, especially if recently issued.' },
];

const embassyNotes = [
  'Germany requires appointments at the German Embassy or VFS Global — walk-ins are not accepted.',
  'Processing time: 15 calendar days standard, 10 days express (additional fee applies).',
  'The Schengen visa fee is €80 for adults, €40 for children aged 6–12.',
  'Appointments must be booked 3–6 weeks in advance during peak summer season.',
];

const riskWarnings = [
  { level: 'high', text: 'Bank statements older than 3 months are a leading cause of rejection at German consulates.' },
  { level: 'medium', text: 'Travel insurance with coverage under €30,000 will result in automatic rejection.' },
  { level: 'low', text: 'Applicants with no prior Schengen history may face additional scrutiny — a strong cover letter helps significantly.' },
];

export default function Step4Requirements({ data, onNext, onBack }: Props) {
  const [loading, setLoading] = useState(true);
  const [showOptional, setShowOptional] = useState(false);
  const [expandedNote, setExpandedNote] = useState<string | null>(null);

  useEffect(() => {
    // TODO: Call /api/visa/requirements with { country, visaType, nationality }
    const timer = setTimeout(() => setLoading(false), 2200);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="wizard-card space-y-6 animate-slide-up">
        <div className="text-center space-y-4 py-8">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto">
            <Sparkles size={28} className="text-blue-500 animate-pulse" />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-extrabold text-gray-900">AI is analyzing requirements...</h2>
            <p className="text-sm text-gray-500">
              Checking {data.destinationFlag} {data.destinationCountry} embassy rules for {data.visaType} visa
            </p>
          </div>
          <div className="space-y-2 max-w-xs mx-auto">
            {[
              'Checking embassy database...',
              'Analyzing your nationality profile...',
              'Generating personalized checklist...',
            ].map((msg, idx) => (
              <div key={`loading-msg-${idx}`} className="flex items-center gap-2 text-xs text-gray-400">
                <div className="w-3 h-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin shrink-0" />
                {msg}
              </div>
            ))}
          </div>
        </div>
        {/* Skeleton */}
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={`skel-req-${i + 1}`} className="h-14 rounded-xl skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-card space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Step 4 of 9</p>
          <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">Your visa requirements</h2>
          <p className="text-sm text-gray-500">
            AI-generated for {data.destinationFlag} {data.destinationCountry} — {data.visaType} visa
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 border border-blue-100 rounded-lg shrink-0">
          <Sparkles size={12} className="text-blue-500" />
          <span className="text-xs font-bold text-blue-700">AI Generated</span>
        </div>
      </div>

      {/* Risk warnings */}
      <div className="space-y-2">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Risk alerts</p>
        {riskWarnings.map((w, idx) => (
          <div
            key={`risk-${idx}`}
            className={`flex items-start gap-2.5 p-3 rounded-xl border text-sm ${
              w.level === 'high' ?'bg-red-50 border-red-100 text-red-800'
                : w.level === 'medium' ?'bg-amber-50 border-amber-100 text-amber-800' :'bg-blue-50 border-blue-100 text-blue-800'
            }`}
          >
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span className="text-xs font-medium leading-relaxed">{w.text}</span>
          </div>
        ))}
      </div>

      {/* Required documents */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Required documents</p>
          <span className="badge-blue">{requiredDocs.length} items</span>
        </div>
        <div className="space-y-2">
          {requiredDocs.map((doc) => (
            <div
              key={doc.id}
              className="border border-gray-100 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setExpandedNote(expandedNote === doc.id ? null : doc.id)}
                className="w-full flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors text-left"
              >
                <CheckCircle2 size={16} className="text-blue-400 shrink-0" />
                <span className="text-sm font-semibold text-gray-800 flex-1">{doc.label}</span>
                {doc.aiGenerated && (
                  <span className="badge-blue text-xs shrink-0">AI writes this</span>
                )}
                {doc.critical && !doc.aiGenerated && (
                  <span className="badge-red text-xs shrink-0">Required</span>
                )}
                {expandedNote === doc.id ? <ChevronUp size={14} className="text-gray-400 shrink-0" /> : <ChevronDown size={14} className="text-gray-400 shrink-0" />}
              </button>
              {expandedNote === doc.id && (
                <div className="px-4 pb-3 animate-fade-in">
                  <p className="text-xs text-gray-500 leading-relaxed pl-7">{doc.detail}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Optional documents */}
      <div className="space-y-2">
        <button
          onClick={() => setShowOptional(!showOptional)}
          className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-widest hover:text-gray-700 transition-colors"
        >
          {showOptional ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          Optional documents ({optionalDocs.length})
        </button>
        {showOptional && (
          <div className="space-y-2 animate-fade-in">
            {optionalDocs.map((doc) => (
              <div key={doc.id} className="flex items-start gap-3 p-3.5 bg-gray-50 border border-gray-100 rounded-xl">
                <Info size={15} className="text-gray-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-700">{doc.label}</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{doc.detail}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Embassy notes */}
      <div className="space-y-2">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Embassy notes</p>
        <div className="space-y-1.5">
          {embassyNotes.map((note, idx) => (
            <div key={`embassy-note-${idx}`} className="flex items-start gap-2 text-xs text-gray-600">
              <Clock size={12} className="text-blue-400 shrink-0 mt-0.5" />
              <span className="leading-relaxed">{note}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={onBack} className="btn-secondary py-2.5 px-4 text-sm">
          <ChevronLeft size={15} />
          Back
        </button>
        <button onClick={onNext} className="btn-primary flex-1 justify-center py-2.5 text-sm">
          Upload my documents
          <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}